import { isFeatureFlagEnabled } from "../../../shared/lib/posthog";
import { useInstitution } from "./useInstitution";
import { usePermission } from "./usePermission";

export async function useNavigation() {
	const { hasPermission } = usePermission();

	const { waitForInstitutionConfig, isEAorIA, isEA, institutionConfig } =
		useInstitution();
	// Single call to get all navigation data
	// Extract all the data from the single response
	await waitForInstitutionConfig();

	const showApprovalPage = (() => {
		if (institutionConfig.value.approval_config === "adoption_approvals") {
			return {
				title: "Approvals",
				to: "/approvals",
			};
		}
		return {
			title: "Requests",
			to: "/requests",
		};
	})();

	const items = [
		{
			to: "/adoptions",
			title: "Adoptions",
			icon: "/student.svg",
			guard:
				hasPermission(
					institutionConfig.value.adminUserPermissions,
					"adoptions:viewAssignedCourses",
				) && isEAorIA.value,
		},
		{
			to: "/catalog-search",
			title: "Search",
			icon: "/search.svg",
			guard: hasPermission(
				institutionConfig.value.adminUserPermissions,
				"catalogue:search",
			),
		},
		{
			to: "/booklist",
			title: "Booklist",
			icon: "/booklist.svg",
			guard: hasPermission(
				institutionConfig.value.adminUserPermissions,
				"booklist:view",
			),
			subItems: [
				{
					to: "/booklist/supply",
					title: "Supplies",
					guard:
						!institutionConfig.value.isPricingApprovalsEnabled &&
						hasPermission(
							institutionConfig.value.adminUserPermissions,
							"booklist:view",
						) &&
						institutionConfig.value.enable_supply_request === "enabled",
				},
				{
					to: "/booklist/upload",
					title: "Bulk Upload",
					guard: hasPermission(
						institutionConfig.value.adminUserPermissions,
						"booklist:bulkUpload",
					),
				},
				{
					to: "/booklist/new-request",
					title: "New Request",
					guard:
						!institutionConfig.value.isPricingApprovalsEnabled &&
						hasPermission(
							institutionConfig.value.adminUserPermissions,
							"booklist:request",
						),
				},
			],
		},
		{
			to:
				(institutionConfig.value.availableDashboards?.length ?? 0) > 0
					? `/analytics/${institutionConfig.value.availableDashboards[0].id}`
					: "/analytics",
			title: "Analytics",
			icon: "/uptrend.svg",
			guard:
				isFeatureFlagEnabled("dashboards") &&
				(institutionConfig.value.availableDashboards?.length ?? 0) > 0,
			subItems: (institutionConfig.value.availableDashboards ?? []).map(
				(dashboard) => ({
					to: `/analytics/${dashboard.id}`,
					title: dashboard.displayName,
				}),
			),
		},
		{
			to: showApprovalPage.to,
			title: showApprovalPage.title,
			icon: "/approvals.svg",
			guard: institutionConfig.value.hasApprovalPermission,
			subItems: [
				{
					to: `${showApprovalPage.to}/history`,
					title: "History",
					guard: institutionConfig.value.hasApprovalPermission,
				},
				{
					to: "/requests/supply",
					title: "Supplies",
					guard:
						institutionConfig.value.isPricingApprovalsEnabled &&
						hasPermission(
							institutionConfig.value.adminUserPermissions,
							"booklist:view",
						) &&
						institutionConfig.value.enable_supply_request === "enabled",
				},
				{
					to: "/requests/new-request",
					title: "New Request",
					guard:
						institutionConfig.value.isPricingApprovalsEnabled &&
						hasPermission(
							institutionConfig.value.adminUserPermissions,
							"booklist:request",
						),
				},
			],
		},
		{
			to: "/courses",
			title: "Courses",
			icon: "/courses.svg",
			guard:
				institutionConfig.value.courseIngestionEnabled &&
				hasPermission(
					institutionConfig.value.adminUserPermissions,
					"adoptions:viewAllCourses",
				),
		},
		{
			to: "/students",
			title: "Students",
			icon: "/students.svg",
			guard:
				isFeatureFlagEnabled("user-management-page") &&
				isEA.value &&
				institutionConfig.value.opt_out_hosting_type === "bibliu-hosted",
		},
		{
			to: "/reports",
			title: "Reports",
			icon: "/reports.svg",
			guard:
				hasPermission(
					institutionConfig.value.adminUserPermissions,
					"counterReport:view",
				) &&
				(institutionConfig.value.region?.toLowerCase() === "gb" ||
					["malaysiamoh", "bibliotechdemoinstitution", "bibliu"].includes(
						institutionConfig.value.institution_id?.toLowerCase(),
					)),
		},
		{
			to: "/store/configuration",
			title: "Store",
			icon: "/reports.svg",
			guard:
				institutionConfig.value.region?.toLowerCase() === "us" &&
				(isFeatureFlagEnabled("store-management") ||
					hasPermission(
						institutionConfig.value.adminUserPermissions,
						"campus-store:manage",
					)),
			subItems: [
				{
					to: "/store/configuration",
					title: "Configuration",
					guard:
						institutionConfig.value.region?.toLowerCase() === "us" &&
						(isFeatureFlagEnabled("store-management") ||
							hasPermission(
								institutionConfig.value.adminUserPermissions,
								"campus-store:manage",
							)),
				},
				{
					to: "/store/financial-aid",
					title: "Financial Aid",
					guard:
						institutionConfig.value.region?.toLowerCase() === "us" &&
						(isFeatureFlagEnabled("store-management") ||
							hasPermission(
								institutionConfig.value.adminUserPermissions,
								"campus-store:manage",
							)),
				},
				{
					to: "/store/courses",
					title: "Courses",
					guard:
						institutionConfig.value.region?.toLowerCase() === "us" &&
						(isFeatureFlagEnabled("store-management") ||
							hasPermission(
								institutionConfig.value.adminUserPermissions,
								"campus-store:manage",
							)),
				},
			],
		},
	];

	/**
	 * Find redirect route based on user permissions
	 */
	function getRedirectUrl() {
		const rolesAndPermissionsFeatureFlag = isFeatureFlagEnabled(
			"roles-and-permissions",
		);

		if (!rolesAndPermissionsFeatureFlag) {
			return "/booklist";
		}

		const hierarchyOrder = [
			"/booklist",
			"/adoptions",
			"/analytics",
			showApprovalPage.to,
			"/catalog-search",
			"/courses",
			"/students",
		];

		// Find the first accessible route in hierarchy order
		for (const path of hierarchyOrder) {
			const item = items.find(
				(item) => item.to === path || item.to.startsWith(path),
			);
			if (item && item?.guard !== false) {
				return item.to;
			}
		}

		// Fallback to original logic if no accessible items found from hierarchy
		const topLevelRoutesWithPermissions = items.find(
			({ to, guard }) => to.match(/\//g)?.length === 1 && guard !== false,
		);

		const redirectPath = topLevelRoutesWithPermissions
			? `/${topLevelRoutesWithPermissions.to.split("/")[1]}`
			: "/unauthorized";

		return redirectPath;
	}

	return {
		items,
		adminUserPermissions: institutionConfig.value.adminUserPermissions,
		availableDashboards: institutionConfig.value.availableDashboards ?? [],
		hasApprovalPermission: institutionConfig.value.hasApprovalPermission,
		showApprovalPage,
		isPricingApprovalsEnabled:
			institutionConfig.value.isPricingApprovalsEnabled,
		getRedirectUrl,
	};
}
